///<reference types="@types/googlemaps"/>

import { Injectable, NgZone } from '@angular/core';
import { GasStationModel } from '@core/api';
import { Subject } from 'rxjs/internal/Subject';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { StationService } from '@accounts/user/services/station.service';
import { TariffService } from '@accounts/user/services/tariff.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { environment } from '../../../../environments/environment';
import { LatLngBounds } from '@agm/core'
import {ActivatedRoute} from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class MapService {
  lat = environment.map.lat;
  lng = environment.map.lng;
  zoom = environment.map.zoom;

  map = null;
  markersId = new Set<number>();

  markers = new Set<GasStationModel>();
  stations = new Set<GasStationModel>();

  debouncer= new Subject();

  isSmallScreen: boolean;

  constructor(
    public breakpointObserver: BreakpointObserver,
    private stationService: StationService,
    private tariffService: TariffService,
    private ngZone: NgZone
  ) {
    this.initDebounce();

    this.breakpointObserver.observe([
      Breakpoints.XSmall,
      Breakpoints.Small
    ]).subscribe(state => {
      this.isSmallScreen = state.matches;
    });

    // Subscribe to location filter changes
    this.stationService.locationFilter$.subscribe(() => {
      this.applyLocationFilter();
    });
  }

  mapReady() {
    this.map = new ymaps.Map("map", {
      center: [this.lat, this.lng],
      zoom: this.zoom,
      controls: ['zoomControl']
    });
    this.map.events.add('boundschange',  () => {
      this.ngZone.run(() => {
        this.boundsChange(this.map.getBounds())
      })
    })
    this.getStationByBounce(this.map.getBounds())

    if (this.stationService.station) {
      this.goToCurrentStation(
        this.stationService.station.coordinate_latitude,
        this.stationService.station.coordinate_longitude
      );
    }
  }

  initDebounce() {
    this.debouncer
      .pipe(
        debounceTime(350),
        distinctUntilChanged()
      )
      .subscribe((val) => this.getStationByBounce(val));
  }

  getStationByBounce(bounds) {
    const  [[loc_s,  loc_w], [ loc_n, loc_e ]] = bounds;

    this.stationService.getStationByBounce(loc_w, loc_e, loc_n, loc_s)
      .subscribe(data => {
        this.stations.clear();
        data.forEach(station => {
          if(!this.markersId.has(station.id)) {
            this.markers.add(station);
            this.markersId.add(station.id);
          }
          this.stations.add(station);
        });
        this.applyLocationFilter();
      });
  }

  applyLocationFilter() {
    if (!this.map) return;

    const filter = this.stationService.locationFilter;
    let filteredMarkers = Array.from(this.markers);

    // If both city and return location are selected
    if (filter.cityStationIds && filter.cityStationIds.length > 0 && filter.returnLocationId !== null) {
      // Check if return station is in the rental city
      const isReturnInRentalCity = filter.cityStationIds.includes(filter.returnLocationId);
      
      if (isReturnInRentalCity) {
        // Same city return - show only the return station
        filteredMarkers = filteredMarkers.filter(station => 
          station.id === filter.returnLocationId
        );
      } else {
        // Different city return - show all rental city stations
        // (they can all return trailers to the transit location)
        filteredMarkers = filteredMarkers.filter(station => 
          filter.cityStationIds.includes(station.id)
        );
      }
    } 
    // If only city is selected, show all stations in the city
    else if (filter.cityStationIds && filter.cityStationIds.length > 0) {
      filteredMarkers = filteredMarkers.filter(station => 
        filter.cityStationIds.includes(station.id)
      );
    }

    this.showMarkers(new Set(filteredMarkers), this.map);
  }

  boundsChangeSearch(bounds){
      this.boundsChange(bounds);
      this.goToCurrentBounds(bounds);
  }

  boundsChange(val) {
    this.debouncer.next(val);
  }

  goToCurrentBounds(bounds) {
    this.map.setBounds(bounds);
  }

  goToCurrentStation(lat: number, lng: number) {
    if (this.map) {
      this.map.setZoom(15);
      this.map.panTo([lat, lng + (this.isSmallScreen ? 0 : 0.005)]);
    }
  }

  selectMarker(marker) {
    this.ngZone.run(() => {
      this.stationService.getCurrentStationById(marker.id)
        .subscribe(station => {
          this.boundsChange(this.map.getBounds())
          this.tariffService.changeCurrentTariff(this.tariffService.customTariff.value);
        });
    })
  }

  showMarkers(markers, map) {
    map.geoObjects.removeAll();
    if (markers.size) {
      markers.forEach(marker => {
        const myPlacemark = new ymaps.Placemark([marker.coordinate_latitude, marker.coordinate_longitude], {}, {
            iconLayout: 'default#image',
            iconImageHref: marker.id === (this.stationService.station && this.stationService.station.id) ? '/assets/icons/baseline-place-active.svg': '/assets/icons/baseline-place.svg',
            iconImageSize: [30, 42],
        });
        myPlacemark.events.add('click',  () => {           
              this.selectMarker(marker)      
        })
        map.geoObjects.add(myPlacemark);
      })
    } 
  }
}
